import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { apiCall } from '../services/apiUtil';
import { secureStorage } from '../services/secureStorage';

// FCM registration crashes the native process if google-services.json is missing.
// Set VITE_FCM_ENABLED=true once Firebase is configured on both platforms.
const FCM_ENABLED = import.meta.env.VITE_FCM_ENABLED === 'true';

/**
 * Returns true when running as a native iOS or Android app.
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * localStorage keys:
 *   fcm_token_device  — the device's FCM token, permanent, never auto-sent to backend
 *   fcm_token         — set only when user has opted in (backend record exists)
 *
 * This separation means:
 *   - Toggle shows ON  ↔  fcm_token is set
 *   - Toggle shows OFF ↔  fcm_token is absent (even if device has a token)
 *   - New installs and after toggle OFF → toggle starts as OFF
 */

let _listenersAdded = false;
let _registering = false;

/**
 * Register for native push notifications (iOS/Android).
 * - Requests permission and registers with APNs/FCM to get the device token
 * - Stores token as fcm_token_device (never auto-sends to backend)
 * - If the user was previously opted in (fcm_token exists), re-registers with backend
 *   so the token stays fresh across app restarts
 * Safe to call multiple times — listeners are added once.
 */
export async function registerNativePush() {
  if (!isNativePlatform()) return;
  if (_registering) return; // prevent concurrent calls during crash-reload loops
  _registering = true;

  try {
    if (!FCM_ENABLED) {
      console.warn('[NativePush] FCM disabled — set VITE_FCM_ENABLED=true after adding google-services.json / GoogleService-Info.plist');
      return;
    }

    const { receive } = await PushNotifications.requestPermissions();
    if (receive !== 'granted') {
      console.warn('[NativePush] Permission not granted:', receive);
      return;
    }

    // Android 8+ requires a notification channel before the first push arrives
    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel({
        id: 'default',
        name: 'Kynaix Sports',
        description: 'Activity notifications — squad invites, teammate requests, scoring updates',
        importance: 4,
        vibration: true,
        sound: 'default',
        visibility: 1
      });
    }

    if (!_listenersAdded) {
      // Clear any stale native listeners from previous WebView sessions.
      // Without this, crash-reload loops accumulate orphaned iOS native listeners.
      await PushNotifications.removeAllListeners();
      _listenersAdded = true;

      // iOS: Firebase delivers FCM token via window event dispatched from AppDelegate.
      // Listeners must be attached BEFORE calling register() — on iOS, Firebase may
      // deliver the cached token immediately when register() is called, so a listener
      // added after register() would miss the event.
      window.addEventListener('fcmTokenReceived', (e) => {
        if (!e.detail?.token) return;
        const token = e.detail.token;
        localStorage.setItem('fcm_token_device', token);
        registerToken(token);
      });

      // Android: use Capacitor's registration event directly
      await PushNotifications.addListener('registration', async (token) => {
        if (Capacitor.getPlatform() === 'android') {
          localStorage.setItem('fcm_token_device', token.value);
          await registerToken(token.value);
        }
      });

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('[NativePush] Registration error:', err.error);
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[NativePush] Foreground notification:', notification);
        window.dispatchEvent(new CustomEvent('nativePushReceived', { detail: notification }));
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification?.data || {};
        const url = data.url;
        if (url) {
          window.dispatchEvent(new CustomEvent('nativePushTapped', { detail: { url } }));
          return;
        }
        // Stream push payload: data.sender === 'stream.chat', channel info in data.channel_id / data.stream
        if (data.sender === 'stream.chat') {
          let channelId = data.channel_id;
          if (!channelId && data.stream) {
            try { channelId = JSON.parse(data.stream)?.channel_id; } catch {}
          }
          if (channelId) {
            window.dispatchEvent(new CustomEvent('nativePushTapped', { detail: { url: `/messages?channel=${channelId}` } }));
          }
        }
      });
    }

    // register() is called AFTER listeners so the fcmTokenReceived event is never missed.
    await PushNotifications.register();

    // If we already have the device token, re-register with backend to keep it fresh
    // across app restarts. registerToken is a no-op when there is no active session.
    const deviceToken = localStorage.getItem('fcm_token_device');
    if (deviceToken) {
      await registerToken(deviceToken);
    }

  } catch (err) {
    console.error('[NativePush] Setup error:', err);
  } finally {
    _registering = false;
  }
}

/**
 * Send FCM token to backend. Sets fcm_token (opted-in marker) on success.
 * No-op when there is no active session — avoids 401s on app startup before login
 * and prevents the apiCall 401 handler from incorrectly clearing a valid session.
 */
async function registerToken(fcmToken) {
  const sessionToken = await secureStorage.getItem('token');
  if (!sessionToken) return;
  const platform = Capacitor.getPlatform();
  try {
    await apiCall('/device-tokens', {
      method: 'POST',
      body: JSON.stringify({ token: fcmToken, platform })
    });
    localStorage.setItem('fcm_token', fcmToken);
    localStorage.setItem('fcm_token_device', fcmToken);
    console.log('[NativePush] Token registered:', platform, fcmToken.substring(0, 20) + '...');
  } catch (err) {
    console.error('[NativePush] Failed to register token:', err);
  }
}

/**
 * Called immediately after a successful login to auto-register the FCM token.
 * - If the device token is already cached locally, sends it to the backend now.
 * - If not yet obtained, requests permission and triggers FCM registration;
 *   the fcmTokenReceived / registration listeners will complete the backend call.
 * Does NOT update user preferences — the backend defaults push to enabled for
 * users with no preference record.
 */
export async function autoRegisterOnLogin() {
  if (!isNativePlatform()) return;
  if (!FCM_ENABLED) return;

  try {
    const { receive } = await PushNotifications.requestPermissions();
    if (receive !== 'granted') return;
    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel({
        id: 'default',
        name: 'Kynaix Sports',
        description: 'Activity notifications — squad invites, teammate requests, scoring updates',
        importance: 4,
        vibration: true,
        sound: 'default',
        visibility: 1
      });
    }
    // Always call register() so Firebase/APNs issues a fresh token for this build environment.
    // On TestFlight this gets a production APNs-backed token instead of a sandbox one.
    await PushNotifications.register();
    // Register the cached token immediately — Firebase may not re-fire fcmTokenReceived
    // if it already delivered the token at app startup. The fresh token (if different)
    // will arrive via the fcmTokenReceived listener and overwrite this entry.
    const cachedToken = localStorage.getItem('fcm_token_device');
    if (cachedToken) {
      await registerToken(cachedToken);
    }
  } catch (err) {
    console.error('[NativePush] autoRegisterOnLogin error:', err);
  }
}

/**
 * Called from the push toggle to explicitly opt in.
 * Uses the stored device token — no need to wait for Firebase re-delivery.
 */
export async function enableNativePush() {
  if (!isNativePlatform()) return false;
  if (!FCM_ENABLED) return false;

  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== 'granted') return false;

  const deviceToken = localStorage.getItem('fcm_token_device');
  if (!deviceToken) return false;

  await registerToken(deviceToken);
  return !!localStorage.getItem('fcm_token');
}

/**
 * Called from the push toggle to explicitly opt out.
 * Removes the backend record so the user stops receiving notifications.
 * Keeps fcm_token_device so re-enabling works without app restart.
 */
export async function disableNativePush() {
  if (!isNativePlatform()) return;

  const token = localStorage.getItem('fcm_token');
  if (token) {
    await apiCall('/device-tokens', {
      method: 'DELETE',
      body: JSON.stringify({ token })
    }).catch(() => {});
    localStorage.removeItem('fcm_token');
  }
}

/**
 * Set the app icon badge count.
 * iOS: WebKit message handler → Swift UNUserNotificationCenter.setBadgeCount
 * Android: clearing delivered notifications removes the Samsung badge (count=0 only).
 *          Android badges reflect tray notification count — no direct number API.
 */
export function setNativeBadge(count) {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    try {
      window.webkit?.messageHandlers?.setBadge?.postMessage(count);
    } catch (_) {}
  }
  // Android: badge count is tied to the notification tray — no direct number API.
  // Do NOT auto-clear delivered notifications here; they are cleared explicitly via
  // clearAndroidNotifications() when the user actively reads all notifications.
}

/**
 * Clear all delivered notifications from the Android tray (call only on explicit read-all).
 * On Android, tray count drives the launcher badge — clearing the tray resets it to 0.
 */
export function clearAndroidNotifications() {
  if (Capacitor.getPlatform() === 'android') {
    PushNotifications.removeAllDeliveredNotifications().catch(() => {});
  }
}

/**
 * Remove the FCM token on logout so the user stops receiving notifications.
 */
export async function unregisterNativePush() {
  if (!isNativePlatform()) return;
  if (!FCM_ENABLED) return;

  // If push was never registered (no device token), there's nothing to clean up.
  // Calling getDeliveredNotifications() without a prior registration throws
  // "capacitorDidRegisterForRemoteNotifications not called".
  const deviceToken = localStorage.getItem('fcm_token_device');
  if (!deviceToken) return;

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    if (result.notifications.length > 0) {
      await PushNotifications.removeAllDeliveredNotifications();
    }
    await disableNativePush();
    // fcm_token_device is kept so re-login can re-enable without app restart
  } catch (err) {
    // "capacitorDidRegisterForRemoteNotifications not called" is expected when
    // the user logs out before push completes registration in this session.
    console.warn('[NativePush] Unregister error:', err?.message ?? err);
  }
}
